import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Activity, AlertTriangle, Clock } from 'lucide-react';
import { api } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDate } from '@/lib/utils';

// TODO(signals): wire to canonical /api/signals endpoint once backend exposes
// pagination + filtering. For MVP this is a thin table over the list response.

interface Signal {
  id: string;
  activeSubstance: string | null;
  // Canonical Prisma column names (see apps/api/prisma/schema.prisma model Signal).
  meddraPreferredTerm: string | null;
  meddraSoc: string | null;
  detectionSource: string | null;
  validationStatus: string | null;
  priorityLevel: string | null;
  disposition: string | null;
  caseCount: number | null;
  notificationClockDue: string | null;
  notificationSentAt: string | null;
}

export function SignalsPage() {
  const [status, setStatus] = useState('');
  const [priority, setPriority] = useState('');
  const [source, setSource] = useState('');
  const [overdueOnly, setOverdueOnly] = useState(false);

  const signals = useQuery({
    queryKey: ['signals', { status, priority, source }],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('pageSize', '100');
      if (status) params.set('validationStatus', status);
      if (priority) params.set('priority', priority);
      if (source) params.set('source', source);
      try {
        return (await api.get(`/api/signals?${params.toString()}`)).data;
      } catch {
        return { items: [], total: 0 };
      }
    },
  });

  const items: Signal[] = signals.data?.items ?? [];
  const now = useMemo(() => Date.now(), []);
  const filtered = overdueOnly
    ? items.filter(
        (s) =>
          s.notificationClockDue &&
          !s.notificationSentAt &&
          new Date(s.notificationClockDue).getTime() < now,
      )
    : items;

  const overdue = items.filter(
    (s) =>
      s.notificationClockDue &&
      !s.notificationSentAt &&
      new Date(s.notificationClockDue).getTime() < now,
  ).length;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Signal management</h1>
        <p className="text-sm text-muted-foreground">
          Detect, validate, prioritise and close signals per GVP Module IX.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <StatCard icon={Activity} label="Total signals" value={signals.data?.total ?? items.length} />
        <StatCard
          icon={AlertTriangle}
          label="Confirmed"
          value={items.filter((s) => s.validationStatus === 'CONFIRMED').length}
        />
        <StatCard
          icon={Clock}
          label="Overdue clocks"
          value={overdue}
        />
        <StatCard
          icon={Activity}
          label="Under review"
          value={items.filter((s) => s.validationStatus === 'UNDER_REVIEW').length}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
          <CardDescription>Narrow the list by validation status, priority or source.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-4">
            <div>
              <Label htmlFor="vs">Validation status</Label>
              <select
                id="vs"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="">All</option>
                <option value="NEW">New</option>
                <option value="UNDER_REVIEW">Under review</option>
                <option value="CONFIRMED">Confirmed</option>
                <option value="REFUTED">Refuted</option>
                <option value="CLOSED">Closed</option>
              </select>
            </div>
            <div>
              <Label htmlFor="pr">Priority</Label>
              <select
                id="pr"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="">All</option>
                <option value="HIGH">High</option>
                <option value="MEDIUM">Medium</option>
                <option value="LOW">Low</option>
              </select>
            </div>
            <div>
              <Label htmlFor="src">Source</Label>
              <select
                id="src"
                value={source}
                onChange={(e) => setSource(e.target.value)}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="">All</option>
                <option value="SPONTANEOUS">Spontaneous</option>
                <option value="LITERATURE">Literature</option>
                <option value="CLINICAL_TRIAL">Clinical trial</option>
                <option value="REGULATORY">Regulatory authority</option>
                <option value="INTERNAL">Internal</option>
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={overdueOnly}
                  onChange={(e) => setOverdueOnly(e.target.checked)}
                />
                Overdue only
              </label>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Signals</CardTitle>
          <CardDescription>
            Clock dates follow the selected notification type (emerging / confirmed / urgent).
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Active substance</TableHead>
                <TableHead>MedDRA PT</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Validation</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead>Disposition</TableHead>
                <TableHead>Clock due</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {signals.isLoading && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-sm text-muted-foreground">
                    Loading...
                  </TableCell>
                </TableRow>
              )}
              {!signals.isLoading && filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-sm text-muted-foreground">
                    No signals match the current filters.
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((s) => {
                const isOverdue =
                  s.notificationClockDue &&
                  !s.notificationSentAt &&
                  new Date(s.notificationClockDue).getTime() < now;
                return (
                  <TableRow key={s.id}>
                    <TableCell className="font-medium">{s.activeSubstance ?? '-'}</TableCell>
                    <TableCell className="text-sm">{s.meddraPreferredTerm ?? '-'}</TableCell>
                    <TableCell className="text-xs">{s.detectionSource ?? '-'}</TableCell>
                    <TableCell>
                      <ValidationBadge s={s.validationStatus} />
                    </TableCell>
                    <TableCell>
                      <PriorityBadge p={s.priorityLevel} />
                    </TableCell>
                    <TableCell className="text-xs">{s.disposition ?? '-'}</TableCell>
                    <TableCell
                      className={
                        isOverdue
                          ? 'text-xs font-medium text-destructive'
                          : 'text-xs text-muted-foreground'
                      }
                    >
                      {s.notificationClockDue ? formatDate(s.notificationClockDue) : '-'}
                      {isOverdue ? ' (overdue)' : ''}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button asChild variant="outline" size="sm">
                        <Link to={`/signals/${s.id}`}>View</Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number | string;
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-4 p-6">
        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 text-primary">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <div className="text-2xl font-bold">{value}</div>
          <div className="text-xs text-muted-foreground">{label}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function ValidationBadge({ s }: { s: string | null }) {
  if (!s) return <span className="text-muted-foreground">-</span>;
  const v: Record<string, 'secondary' | 'info' | 'success' | 'destructive' | 'warning'> = {
    NEW: 'info',
    UNDER_REVIEW: 'warning',
    CONFIRMED: 'success',
    REFUTED: 'secondary',
    CLOSED: 'secondary',
  };
  return <Badge variant={v[s] ?? 'secondary'}>{s.replace(/_/g, ' ')}</Badge>;
}

function PriorityBadge({ p }: { p: string | null }) {
  if (!p) return <span className="text-muted-foreground">-</span>;
  const v: Record<string, 'destructive' | 'warning' | 'secondary'> = {
    HIGH: 'destructive',
    MEDIUM: 'warning',
    LOW: 'secondary',
  };
  return <Badge variant={v[p] ?? 'secondary'}>{p}</Badge>;
}
